import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { userManagementApi } from '@/services/api/userManagement';
import { User } from '@/services/types/auth';

// Types
export interface UserFilters {
  search: string;
  type: 'all' | 'admin' | 'agent' | 'customer';
  status: 'all' | 'active' | 'inactive';
  startDate?: string;
  endDate?: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

export interface UserStats {
  total: {
    users: number;
    active: number;
    inactive: number;
  };
  byType: {
    admin: { total: number; active: number; inactive: number };
    agent: { total: number; active: number; inactive: number };
    customer: { total: number; active: number; inactive: number };
  };
  recent: {
    total: number;
    admin: number;
    agent: number;
    customer: number;
  };
}

export interface UserActivity {
  id: string;
  userId: string;
  action: string;
  resource: string;
  resourceId?: string;
  details: any;
  ipAddress: string;
  userAgent: string;
  metadata: any;
  createdAt: string;
}

export interface PaginationState {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface UserManagementState {
  users: User[];
  selectedUsers: string[];
  filters: UserFilters;
  pagination: PaginationState;
  userStats: UserStats | null;
  userActivity: UserActivity[];
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  error: string | null;
  currentUser: User | null;
}

const initialState: UserManagementState = {
  users: [],
  selectedUsers: [],
  filters: {
    search: '',
    type: 'all',
    status: 'all',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  },
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  },
  userStats: null,
  userActivity: [],
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  error: null,
  currentUser: null
};

// Async thunks
export const fetchUsers = createAsyncThunk(
  'userManagement/fetchUsers',
  async (params: {
    page?: number;
    limit?: number;
    filters?: Partial<UserFilters>;
  } = {}, { rejectWithValue }) => {
    try {
      // Flatten the parameters - extract filters and merge with other params
      const { filters, ...otherParams } = params;
      const flatParams = {
        ...otherParams,
        ...filters,
        // Remove 'all' values as they're not valid for the API
        type: filters?.type === 'all' ? undefined : filters?.type,
        status: filters?.status === 'all' ? undefined : filters?.status
      };
      const response = await userManagementApi.getUsers(flatParams);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch users');
    }
  }
);

export const fetchUserById = createAsyncThunk(
  'userManagement/fetchUserById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await userManagementApi.getUserById(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch user');
    }
  }
);

export const createUser = createAsyncThunk(
  'userManagement/createUser',
  async (userData: any, { rejectWithValue }) => {
    try {
      const response = await userManagementApi.createUser(userData);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create user');
    }
  }
);

export const updateUser = createAsyncThunk(
  'userManagement/updateUser',
  async ({ id, userData }: { id: string; userData: any }, { rejectWithValue }) => {
    try {
      const response = await userManagementApi.updateUser(id, userData);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update user');
    }
  }
);

export const deleteUser = createAsyncThunk(
  'userManagement/deleteUser',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await userManagementApi.deleteUser(id);
      return { id, message: response.message };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete user');
    }
  }
);

export const toggleUserStatus = createAsyncThunk(
  'userManagement/toggleUserStatus',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await userManagementApi.toggleUserStatus(id);
      return { id, isActive: response.data.isActive };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to toggle user status');
    }
  }
);

export const bulkUserOperations = createAsyncThunk(
  'userManagement/bulkUserOperations',
  async ({ operation, userIds, data }: {
    operation: 'activate' | 'deactivate' | 'delete' | 'update';
    userIds: string[];
    data?: any;
  }, { rejectWithValue }) => {
    try {
      const response = await userManagementApi.bulkUserOperations(operation, userIds, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to perform bulk operation');
    }
  }
);

export const fetchUserStats = createAsyncThunk(
  'userManagement/fetchUserStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await userManagementApi.getUserStats();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch user stats');
    }
  }
);

export const fetchUserActivity = createAsyncThunk(
  'userManagement/fetchUserActivity',
  async ({ id, params }: { id: string; params?: any }, { rejectWithValue }) => {
    try {
      const response = await userManagementApi.getUserActivity(id, params);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch user activity');
    }
  }
);

const userManagementSlice = createSlice({
  name: 'userManagement',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<UserFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
    setSelectedUsers: (state, action: PayloadAction<string[]>) => {
      state.selectedUsers = action.payload;
    },
    toggleUserSelection: (state, action: PayloadAction<string>) => {
      const userId = action.payload;
      const index = state.selectedUsers.indexOf(userId);
      if (index > -1) {
        state.selectedUsers.splice(index, 1);
      } else {
        state.selectedUsers.push(userId);
      }
    },
    clearSelectedUsers: (state) => {
      state.selectedUsers = [];
    },
    setPagination: (state, action: PayloadAction<Partial<PaginationState>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentUser: (state) => {
      state.currentUser = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch users
      .addCase(fetchUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.users = action.payload.data;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Fetch user by ID
      .addCase(fetchUserById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentUser = action.payload.data;
        state.error = null;
      })
      .addCase(fetchUserById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Create user
      .addCase(createUser.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.isCreating = false;
        state.users.unshift(action.payload.data);
        state.pagination.total += 1;
        state.error = null;
      })
      .addCase(createUser.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload as string;
      })
      
      // Update user
      .addCase(updateUser.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.users.findIndex(user => user.id === action.payload.data.id);
        if (index !== -1) {
          state.users[index] = action.payload.data;
        }
        if (state.currentUser?.id === action.payload.data.id) {
          state.currentUser = action.payload.data;
        }
        state.error = null;
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      })
      
      // Delete user
      .addCase(deleteUser.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.users = state.users.filter(user => user.id !== action.payload.id);
        state.pagination.total -= 1;
        state.selectedUsers = state.selectedUsers.filter(id => id !== action.payload.id);
        state.error = null;
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload as string;
      })
      
      // Toggle user status
      .addCase(toggleUserStatus.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(toggleUserStatus.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.users.findIndex(user => user.id === action.payload.id);
        if (index !== -1) {
          state.users[index].isActive = action.payload.isActive;
        }
        state.error = null;
      })
      .addCase(toggleUserStatus.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      })
      
      // Bulk operations
      .addCase(bulkUserOperations.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(bulkUserOperations.fulfilled, (state, action) => {
        state.isUpdating = false;
        // Update users based on bulk operation results
        action.payload.data.results.forEach((result: any) => {
          if (result.success) {
            const index = state.users.findIndex(user => user.id === result.userId);
            if (index !== -1) {
              // Update user based on operation type
              if (action.meta.arg.operation === 'activate') {
                state.users[index].isActive = true;
              } else if (action.meta.arg.operation === 'deactivate') {
                state.users[index].isActive = false;
              } else if (action.meta.arg.operation === 'delete') {
                state.users[index].isActive = false;
              }
            }
          }
        });
        state.selectedUsers = [];
        state.error = null;
      })
      .addCase(bulkUserOperations.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      })
      
      // Fetch user stats
      .addCase(fetchUserStats.fulfilled, (state, action) => {
        state.userStats = action.payload.data;
      })
      
      // Fetch user activity
      .addCase(fetchUserActivity.fulfilled, (state, action) => {
        state.userActivity = action.payload.data;
      });
  }
});

export const {
  setFilters,
  clearFilters,
  setSelectedUsers,
  toggleUserSelection,
  clearSelectedUsers,
  setPagination,
  clearError,
  clearCurrentUser
} = userManagementSlice.actions;

export default userManagementSlice.reducer;
